import {
  type CalendarDate,
  compareDates,
  daysBetween,
  type DateRange,
  fromEpochDay,
  maxDate,
  minDate,
  toEpochDay,
} from '@room-mates/shared';
import { type ReactElement, type ReactNode, useMemo, useState } from 'react';
import { formatDate, getFormatLocale, todayIso } from '../../shared/format.js';
import type { Dictionary } from '../../shared/i18n/dictionary.js';
import { useLanguage, useTranslation } from '../../shared/i18n/useLanguage.js';
import { Button } from '../../shared/ui/index.js';

/** A range being built or edited: either end may still be unset. */
export interface PartialDateRange {
  readonly start: CalendarDate | null;
  readonly end: CalendarDate | null;
}

/**
 * Pass as `bounds` when a picker has no real window of its own to stay
 * within — a sojourn's own dates, say, rather than a stay picked inside one.
 * Wide enough that no household's data can ever reach an edge, narrow enough
 * to stay clear of the legacy two-digit-year handling `Date.UTC` gives values
 * under 100.
 */
export const UNBOUNDED_RANGE: DateRange = { start: '1900-01-01', end: '2999-12-31' };

export interface DateRangeCalendarProps {
  /** No day outside this window (inclusive on both ends) can be picked. */
  readonly bounds: DateRange;
  readonly value: PartialDateRange;
  readonly disabled?: boolean;
  /** Replaces the default status line ({@link describeSelection}) below the header. */
  readonly caption?: ReactNode;
  onChange(value: PartialDateRange): void;
}

interface MonthKey {
  readonly year: number;
  readonly month: number; // 1-12
}

function monthLabelFormat(): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(getFormatLocale(), {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// Read from a known Monday so the weekday abbreviations follow the chosen
// language without depending on the host timezone.
function weekdayLabels(): string[] {
  const format = new Intl.DateTimeFormat(getFormatLocale(), { weekday: 'short', timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, index) =>
    format.format(new Date(Date.UTC(2024, 0, 1 + index))),
  );
}

/**
 * A month grid for picking a stay in two clicks — arrival, then departure —
 * instead of typing three number segments into each of two native date
 * fields.
 *
 * Clicking before the current arrival restarts the range there rather than
 * producing an inverted one, so the calendar can never hand back a range the
 * domain would reject; `describeSelection` below is what callers show next to
 * it while a pick is in progress.
 */
export function DateRangeCalendar({
  bounds,
  value,
  disabled = false,
  caption,
  onChange,
}: DateRangeCalendarProps): ReactElement {
  const { language } = useLanguage();
  const t = useTranslation();
  // Nothing picked yet opens on today's month rather than the start of the
  // bounds, which for a deliberately wide-open picker (a sojourn's own dates,
  // say) would otherwise land the grid over a century away from anything
  // anyone would plausibly be looking for.
  const [visibleMonth, setVisibleMonth] = useState<MonthKey>(() =>
    monthKeyOf(maxDate(bounds.start, minDate(value.start ?? todayIso(), bounds.end))),
  );
  const [hoverDate, setHoverDate] = useState<CalendarDate | null>(null);
  // weekdayLabels() reads the module-level locale set by setFormatLocale(),
  // not `language` itself, so the dependency exists only to force a recompute
  // when the language changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekdays = useMemo(() => weekdayLabels(), [language]);

  const weeks = useMemo(() => buildMonthWeeks(visibleMonth), [visibleMonth]);

  // While only the arrival is chosen, the pointer previews the range that a
  // click on the hovered day would produce.
  const previewEnd = value.start !== null && value.end === null ? hoverDate : null;
  const highlightEnd = value.end ?? previewEnd;

  const earliestMonth = monthKeyOf(bounds.start);
  const latestMonth = monthKeyOf(bounds.end);
  const canGoBack = compareMonths(visibleMonth, earliestMonth) > 0;
  const canGoForward = compareMonths(visibleMonth, latestMonth) < 0;

  // A whole year in one click gets a many-months-away pick done in two clicks
  // instead of a dozen; overshooting the bounds just lands on their edge
  // rather than being refused outright.
  const jump = (months: number): void => {
    setVisibleMonth((month) => clampMonth(shiftMonth(month, months), earliestMonth, latestMonth));
  };

  const selectDay = (day: CalendarDate): void => {
    // No arrival yet, or a complete range already: this click starts over.
    if (value.start === null || value.end !== null) {
      onChange({ start: day, end: null });
      return;
    }
    if (compareDates(day, value.start) <= 0) {
      onChange({ start: day, end: null });
      return;
    }
    onChange({ start: value.start, end: day });
  };

  return (
    <div className="date-calendar">
      <div className="date-calendar__header">
        <div className="date-calendar__nav-group">
          <Button
            small
            variant="ghost"
            type="button"
            aria-label={t.dateRangeCalendar.previousYear}
            disabled={disabled || !canGoBack}
            onClick={() => jump(-12)}
          >
            «
          </Button>
          <Button
            small
            variant="ghost"
            type="button"
            aria-label={t.dateRangeCalendar.previousMonth}
            disabled={disabled || !canGoBack}
            onClick={() => jump(-1)}
          >
            ‹
          </Button>
        </div>
        <strong>{monthLabel(visibleMonth)}</strong>
        <div className="date-calendar__nav-group">
          <Button
            small
            variant="ghost"
            type="button"
            aria-label={t.dateRangeCalendar.nextMonth}
            disabled={disabled || !canGoForward}
            onClick={() => jump(1)}
          >
            ›
          </Button>
          <Button
            small
            variant="ghost"
            type="button"
            aria-label={t.dateRangeCalendar.nextYear}
            disabled={disabled || !canGoForward}
            onClick={() => jump(12)}
          >
            »
          </Button>
        </div>
      </div>

      <p className="date-calendar__status">{caption ?? describeSelection(value, t)}</p>

      <div className="date-calendar__weekdays" aria-hidden="true">
        {weekdays.map((label, index) => (
          <span key={`${label}-${index}`}>{label}</span>
        ))}
      </div>

      <div className="date-calendar__grid" onMouseLeave={() => setHoverDate(null)}>
        {weeks.flatMap((week) =>
          week.map((day) => {
            const dayMonth = monthKeyOf(day);
            const inMonth =
              dayMonth.year === visibleMonth.year && dayMonth.month === visibleMonth.month;
            const withinBounds =
              compareDates(bounds.start, day) <= 0 && compareDates(day, bounds.end) <= 0;
            const selectable = inMonth && withinBounds && !disabled;

            const isStart = value.start === day;
            const isEnd = highlightEnd !== null && highlightEnd === day;
            const inRange =
              value.start !== null &&
              highlightEnd !== null &&
              compareDates(value.start, day) <= 0 &&
              compareDates(day, highlightEnd) <= 0;

            const classes = ['date-calendar__day'];
            if (!inMonth) {
              classes.push('date-calendar__day--outside');
            }
            if (isStart) {
              classes.push('date-calendar__day--start');
            }
            if (isEnd) {
              classes.push('date-calendar__day--end');
            }
            if (inRange && !isStart && !isEnd) {
              classes.push('date-calendar__day--in-range');
            }

            return (
              <button
                key={day}
                type="button"
                className={classes.join(' ')}
                disabled={!selectable}
                aria-pressed={isStart || isEnd}
                aria-label={formatDate(day)}
                onMouseEnter={() => {
                  if (selectable) {
                    setHoverDate(day);
                  }
                }}
                onClick={() => selectDay(day)}
              >
                {inMonth ? Number(day.slice(-2)) : ''}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}

/** What to show next to the calendar while a pick is in progress. */
export function describeSelection(value: PartialDateRange, t: Dictionary): string {
  if (value.start === null) {
    return t.dateRangeCalendar.pickArrival;
  }
  if (value.end === null) {
    return t.dateRangeCalendar.pickDeparture(formatDate(value.start));
  }
  return t.dateRangeCalendar.summary(
    formatDate(value.start),
    formatDate(value.end),
    t.count.nights(daysBetween(value.start, value.end)),
  );
}

function monthKeyOf(date: CalendarDate): MonthKey {
  const [year, month] = date.split('-').map(Number) as [number, number];
  return { year, month };
}

function firstDayOf(key: MonthKey): CalendarDate {
  return `${String(key.year).padStart(4, '0')}-${String(key.month).padStart(2, '0')}-01`;
}

function shiftMonth(key: MonthKey, delta: number): MonthKey {
  const total = key.year * 12 + (key.month - 1) + delta;
  return { year: Math.floor(total / 12), month: (((total % 12) + 12) % 12) + 1 };
}

function compareMonths(a: MonthKey, b: MonthKey): number {
  return a.year * 12 + a.month - (b.year * 12 + b.month);
}

function clampMonth(key: MonthKey, earliest: MonthKey, latest: MonthKey): MonthKey {
  if (compareMonths(key, earliest) < 0) {
    return earliest;
  }
  if (compareMonths(key, latest) > 0) {
    return latest;
  }
  return key;
}

function monthLabel(key: MonthKey): string {
  return monthLabelFormat().format(new Date(Date.UTC(key.year, key.month - 1, 1)));
}

/**
 * Monday = 0 … Sunday = 6, computed straight from the epoch day so it never
 * depends on the host timezone the way `Date#getDay` would.
 */
function mondayIndex(epochDay: number): number {
  // Epoch day 0 (1970-01-01) was a Thursday: Sunday-based index 4.
  const sundayIndex = (((epochDay + 4) % 7) + 7) % 7;
  return (sundayIndex + 6) % 7;
}

/**
 * Every date cell for a month view, in Monday-first weeks, padded with the
 * edges of the adjacent months so every row is a full week.
 */
function buildMonthWeeks(key: MonthKey): CalendarDate[][] {
  const firstEpoch = toEpochDay(firstDayOf(key));
  const nextMonthFirstEpoch = toEpochDay(firstDayOf(shiftMonth(key, 1)));
  const lastEpoch = nextMonthFirstEpoch - 1;

  const gridStart = firstEpoch - mondayIndex(firstEpoch);
  const gridEnd = lastEpoch + (6 - mondayIndex(lastEpoch));

  const days: CalendarDate[] = [];
  for (let epoch = gridStart; epoch <= gridEnd; epoch += 1) {
    days.push(fromEpochDay(epoch));
  }

  const weeks: CalendarDate[][] = [];
  for (let index = 0; index < days.length; index += 7) {
    weeks.push(days.slice(index, index + 7));
  }
  return weeks;
}
